/* eslint-disable @reatom/unit-naming-rule */

import { action, atom, reaction } from '@reatom/framework'
import BigNumber from 'bignumber.js'
import i18next from 'i18next'

import { Data, Iterable, NumericFormat, pipe, Result } from '@blade/std-lib'
import { InsufficientBalanceLightError } from '@root/features/trade/validation-errors'
import { Error } from '@root/shared'
import { reatomRequest } from '@root/shared/lib'
import { showErrorSnackbarAction } from '@root/shared/ui'
import { showSnackbarAction } from '@root/shared/ui-kit'

import { processDistributeFundsAction } from './distribute-requests'
import { reatomDistributeAllocatedAmount } from './reatom-distribute-allocated-amount'
import { reatomDistributeTarget } from './reatom-distribute-target'

import type { DistributeTargetModel } from './reatom-distribute-target'
import type {
  DistributionConfirmCreateData,
  DistributionPrepareCreatePayload,
} from '@blade/trade-api-client'
import type { CurrencyModel } from '@root/entities/currency'
import type { WalletModel } from '@root/entities/wallet'
import type { UnknownError } from '@root/shared'

export class NoWalletSelected extends Data.TaggedError('NoWalletSelected') {}
export class NoTargetsSelected extends Data.TaggedError('NoTargetsSelected') {}
export class InsufficientAllocatedAmount extends Data.TaggedError(
  'InsufficientAllocatedAmount',
) {}

export const reatomDistributeFunds = ({
  sourceWallet,
}: {
  sourceWallet?: WalletModel
}) => {
  const sourceWalletAtom = atom<WalletModel | undefined>(sourceWallet)
  const currencyAtom = atom<CurrencyModel | undefined>(undefined)

  const targetsAtom = atom<Array<DistributeTargetModel>>([])

  const processAction = action(
    async (
      ctx,
    ): Promise<Result.Result<DistributionConfirmCreateData, UnknownError>> => {
      const sourceWallet = ctx.get(sourceWalletAtom)
      const targets = ctx.get(targetsAtom)
      const currency = ctx.get(currencyAtom)
      if (!sourceWallet || targets.length === 0 || !currency) {
        return Result.fail(Error.UnknownError.fromUnknown('invalid input'))
      }

      const distributionItems: DistributionPrepareCreatePayload['distributionItems'] =
        targets.map((target) => ({
          walletId: target.wallet.id,
          amount: ctx.get(target.amountAtom).toNumber(),
        }))

      return processDistributeFundsAction(ctx, {
        sourceWalletId: sourceWallet.id,
        network: sourceWallet.network,
        tokenAddress: currency.address,
        distributionItems,
        priorityFee: undefined,
      })
    },
  )

  const requestState = reatomRequest({
    execAction: processAction,
    name: 'reatomDistributeFunds',
    onResult: (ctx, result) => {
      if (Result.isSuccess(result)) {
        showSnackbarAction(ctx, {
          text: i18next.t('wallet.distribute.processing-title'),
          intent: 'info',
        })
        return
      }
      showErrorSnackbarAction(ctx, result.failure)
    },
  })

  const assetAtom = atom((ctx) => {
    const sourceWallet = ctx.spy(sourceWalletAtom)
    const currency = ctx.spy(currencyAtom)
    if (!sourceWallet || !currency) {
      return undefined
    }
    const asset = ctx.spy(sourceWallet.assetsByCurrency)[currency.address]
    return asset
  })

  const availableBalanceAtom = atom((ctx) => {
    const asset = ctx.spy(assetAtom)
    if (!asset) {
      return BigNumber(0)
    }
    const sourceWalletBalance = ctx.spy(asset.balance)
    return sourceWalletBalance
  })

  const setSourceWalletAction = action((ctx, wallet: WalletModel) => {
    sourceWalletAtom(ctx, wallet)
  })

  const amountToAllocateAtom = reatomDistributeAllocatedAmount({
    assetAtom,
  })

  const validationErrorAtom = atom((ctx) => {
    const sourceWallet = ctx.spy(sourceWalletAtom)
    if (!sourceWallet) {
      return new NoWalletSelected()
    }

    const availableBalance = ctx.spy(availableBalanceAtom)
    if (availableBalance.lt(0)) {
      return new InsufficientBalanceLightError()
    }

    const allocatedAmount = ctx.spy(amountToAllocateAtom)
    const targets = ctx.spy(targetsAtom)
    if (allocatedAmount.eq(0) || targets.length === 0) {
      return new NoTargetsSelected()
    }

    const distributedAmount = ctx.spy(distributedAmountAtom)
    if (distributedAmount.gt(allocatedAmount)) {
      return new InsufficientAllocatedAmount()
    }

    return undefined
  })

  const toggleTargetAction = action((ctx, wallet: WalletModel) => {
    const targets = ctx.get(targetsAtom)
    const existingTarget = targets.find(
      (target) => target.wallet.id === wallet.id,
    )
    if (existingTarget) {
      targetsAtom(
        ctx,
        targets.filter((target) => target.wallet.id !== wallet.id),
      )
      return
    }

    const target = reatomDistributeTarget({
      wallet,
      allocatedAmountAtom: amountToAllocateAtom,
    })
    targetsAtom(ctx, [...targets, target])
  })

  const currencyAssetAtom = atom((ctx) => {
    const sourceWallet = ctx.spy(sourceWalletAtom)
    const currency = ctx.spy(currencyAtom)
    if (!sourceWallet || !currency) {
      return undefined
    }
    const byCurrency = ctx.spy(sourceWallet.assetsByCurrency)
    return byCurrency[currency.address]
  })

  const setTargetsAction = action((ctx, wallets: Array<WalletModel>) => {
    const currentTargets = ctx.get(targetsAtom)
    const updatedTargets: Array<DistributeTargetModel> = []
    for (const wallet of wallets) {
      const currentTarget = currentTargets.find(
        (t) => t.wallet.id === wallet.id,
      )

      if (currentTarget) {
        updatedTargets.push(currentTarget)
        continue
      }

      updatedTargets.push(
        reatomDistributeTarget({
          wallet: wallet,
          allocatedAmountAtom: amountToAllocateAtom,
        }),
      )
    }

    targetsAtom(ctx, updatedTargets)
  })

  const manualTargetsAtom = atom((ctx) => {
    const targets = ctx.spy(targetsAtom)

    return targets.filter((target) => {
      const source = ctx.spy(target.inputSource)
      return source !== 'auto'
    })
  })

  const autoTargetsAtom = atom((ctx) => {
    const targets = ctx.spy(targetsAtom)

    return targets.filter((target) => {
      const source = ctx.spy(target.inputSource)
      return source === 'auto'
    })
  })

  const distributedAmountAtom = atom((ctx) => {
    const targets = ctx.spy(targetsAtom)
    return pipe(
      targets,
      Iterable.reduce(BigNumber(0), (acc, target) => {
        const amount = ctx.spy(target.resolvedAmount)
        return acc.plus(amount)
      }),
    )
  })

  const manualTargetsPercentageAtom = atom((ctx) => {
    const manualTargets = ctx.spy(manualTargetsAtom)
    return pipe(
      manualTargets,
      Iterable.reduce(BigNumber(0), (acc, target) => {
        const percentage = ctx.spy(target.resolvedPercentage)
        return acc.plus(percentage)
      }),
    )
  })

  /**
   * Amount left over for auto targets
   */
  const autoAmountAtom = atom((ctx) => {
    const allocatedAmount = ctx.spy(amountToAllocateAtom)
    const manualTargets = ctx.spy(manualTargetsAtom)

    return pipe(
      manualTargets,
      Iterable.reduce(allocatedAmount, (acc, target) => {
        const amount = ctx.spy(target.resolvedAmount)
        return acc.minus(amount)
      }),
      (result) => (result.isNegative() ? BigNumber(0) : result),
    )
  })
  const autoPercentageAtom = atom((ctx) => {
    const manualTargetsPercentage = ctx.spy(manualTargetsPercentageAtom)
    return BigNumber(100).minus(manualTargetsPercentage)
  })

  const balanceTargetReaction = reaction((ctx) => {
    const autoAmount = ctx.spy(autoAmountAtom)
    const autoTargets = ctx.spy(autoTargetsAtom)
    const autoPercentage = ctx.spy(autoPercentageAtom)

    const amountPerWallet = autoAmount.dividedBy(autoTargets.length)
    const percentagePerWallet = autoPercentage.dividedBy(autoTargets.length)

    for (const autoTarget of autoTargets) {
      autoTarget.amountAtom.setRaw(
        ctx,
        NumericFormat.formatPreset(amountPerWallet, 'full'),
      )
      autoTarget.percentageAtom.setRaw(
        ctx,
        NumericFormat.formatPreset(percentagePerWallet, 'full'),
      )
    }
  })

  const initializeAction = action((ctx) => {
    const sub = balanceTargetReaction(ctx)
    return () => {
      sub.unsubscribe()
    }
  })

  return {
    requestState,
    currency: currencyAtom,
    currencyAsset: currencyAssetAtom,
    sourceWallet: sourceWalletAtom,
    setSourceWallet: setSourceWalletAction,
    targets: targetsAtom,
    toggleTarget: toggleTargetAction,
    setTargets: setTargetsAction,
    availableBalance: availableBalanceAtom,
    validationError: validationErrorAtom,
    amountToAllocate: amountToAllocateAtom,
    initialize: initializeAction,
  }
}

export type DistributeFundsModel = ReturnType<typeof reatomDistributeFunds>
